import { useId, type ReactNode } from "react";
import { FORM_HELP_TEXT_CLASS, FORM_LABEL_WITH_SPACING_CLASS } from "../styles/formStyles";
import { classNames } from "../styles/classNames";

/** What a field hands its control, so the label, hint and errors are tied to it. */
export interface FieldControlProps {
  readonly id: string;
  readonly "aria-describedby"?: string;
}

interface FieldProps {
  label: ReactNode;
  /** Kept for screen readers only, where the control's placement already says what it is. */
  labelHidden?: boolean;
  /** `stacked`: label above the control. `inline`: label beside it, in a row of controls. */
  layout?: "stacked" | "inline";
  /** The control's ID, when something else (a test, a link) needs to name it. */
  id?: string;
  /** A short line under the control that says what to enter. */
  hint?: ReactNode;
  /** The field's problems, such as FieldProblems, read with the control. */
  errors?: ReactNode;
  /** Buttons beside the label that fill or reset the control, such as templates. */
  actions?: ReactNode;
  hidden?: boolean;
  /** Layout only (grid span, width); the look is the same for every field. */
  className?: string;
  /** The one control, given the props that tie it to the label, hint and errors. */
  children: (control: FieldControlProps) => ReactNode;
}

/**
 * One labelled control: a text input, select or text area with its label and an optional
 * hint and problems. The label names the control by `htmlFor`, and the hint and problems
 * describe it by `aria-describedby`. For a group of checkboxes or radio buttons use
 * FieldGroup with OptionLabel.
 */
export default function Field({ label, labelHidden = false, layout = "stacked", id, hint, errors, actions, hidden, className, children }: FieldProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const hintId = hint === undefined ? undefined : `${controlId}-hint`;
  const errorsId = errors === undefined || errors === null || errors === false ? undefined : `${controlId}-errors`;
  const describedBy = [hintId, errorsId].filter((part) => part !== undefined).join(" ");
  const control: FieldControlProps = {
    id: controlId,
    ...(describedBy === "" ? {} : { "aria-describedby": describedBy }),
  };
  const inline = layout === "inline";
  const labelClass = labelHidden ? "sr-only" : inline ? "shrink-0" : FORM_LABEL_WITH_SPACING_CLASS;
  return (
    <div className={classNames(inline ? "flex flex-wrap items-center gap-2" : "block text-sm", className)} hidden={hidden}>
      {actions === undefined ? (
        <label htmlFor={controlId} className={labelClass}>{label}</label>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label htmlFor={controlId} className={labelClass}>{label}</label>
          <div className="flex flex-wrap gap-2">{actions}</div>
        </div>
      )}
      {children(control)}
      {hintId === undefined ? null : <p id={hintId} className={classNames(FORM_HELP_TEXT_CLASS, inline && "basis-full")}>{hint}</p>}
      {errorsId === undefined ? null : <div id={errorsId} className={inline ? "basis-full" : undefined}>{errors}</div>}
    </div>
  );
}
